import React, { ReactNode } from 'react'
import { EmailShareButton } from 'react-share'
import { VisuallyHidden, useIsServerEffect } from '@overdose/components'
import { IconLink, IconMail, IconPrinter, IconShare } from '@tabler/icons-react'
import classNames from 'classnames'
import { Button } from '~/components/Button'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { usePage } from '~/hooks'
import { useToast } from '~/hooks/useToast'
import { MakerHubArticleTitleProps } from './MakerHubArticleTitle.types'

const MakerHubArticleTitle = ({
  title = 'How to Set Up a Wi-Fi Range Extender',
  lastModifiedAt,
  className,
}: MakerHubArticleTitleProps) => {
  const shareButtonStyles = {
    '--btn-icon-size': '24px',
  }

  const { showNotification, closeNotification } = useToast()
  const isServer = useIsServerEffect()
  const page = usePage()

  const currentURL = page?.head?.canonical
  const showCopyButton =
    !isServer && navigator.clipboard && window.isSecureContext
  const showShareButton = !isServer && navigator.canShare?.({ url: currentURL })

  const onClickControlBarButton = async (
    action: 'share' | 'print' | 'copyLink'
  ) => {
    if (!navigator || isServer) {
      return null
    }

    switch (action) {
      case 'share': {
        return navigator.share({ url: currentURL })
      }
      case 'print': {
        return print()
      }
      case 'copyLink': {
        await navigator.clipboard.writeText(currentURL)
        return showNotification({
          children: 'Link Copied to Clipboard',
          autoClose: true,
          id: 'copy-to-clipboard-success',
          type: 'info',
          onClose: closeNotification,
        })
      }
      default:
        return null
    }
  }

  const renderDate = (dateString) => {
    const date = new Date(dateString)

    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    return `Updated ${months[date.getMonth()]} ${date.getFullYear()}`
  }

  const renderShareButton = (
    icon: ReactNode,
    seoText: string,
    onClick?: () => void
  ) => {
    return (
      <Button
        size='xs'
        onClick={onClick}
        icon={icon}
        iconOnly
        shape='circle'
        variant='ghost'
        title={seoText}
        style={shareButtonStyles}>
        <VisuallyHidden>{seoText}</VisuallyHidden>
      </Button>
    )
  }

  return (
    <div className={classNames(className)}>
      <Typography
        className={classNames('pb-4')}
        tag={TypographyTag.h1}
        variant={TypographyVariant.Heading4}>
        {title}
      </Typography>
      <div
        className={classNames(
          'text-greyscale-700 flex items-center justify-between overflow-hidden'
        )}>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyRegular}>
          {renderDate(lastModifiedAt)}
        </Typography>
        <div className={classNames('flex items-center gap-4')}>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodyRegular}>
            Share
          </Typography>
          <div className={classNames('flex gap-2')}>
            {showShareButton &&
              renderShareButton(<IconShare />, 'Share article', () => {
                onClickControlBarButton('share')
              })}
            {showCopyButton &&
              renderShareButton(<IconLink />, 'Copy link to article', () => {
                onClickControlBarButton('copyLink')
              })}
            {renderShareButton(<IconPrinter />, 'Print article', () => {
              onClickControlBarButton('print')
            })}
            <EmailShareButton
              url={currentURL}
              className='focus-visible:outline focus-visible:outline-1 focus-visible:outline-interactive-action-focus focus:outline focus:outline-interactive-action-focus rounded-full p-px'
              children={<IconMail className='text-primary-blue ' />}
              title='Email article'
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default MakerHubArticleTitle
